import type { ChildId } from './valueObjects/childId';
import type { MealContent } from './valueObjects/mealContent';
import type { MeasureDateTime } from './valueObjects/measureDateTime';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { ParentId } from './valueObjects/parentId';
import type { MealJsonPub, MealJsonPvt } from '@/types/domain/meal/entities/mealEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 食事プロパティ非公開
 */
export type MealPropsPvt = {
  /** 測定日時 */
  measureDateTime: MeasureDateTime,
  /** 測定者ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 食事内容 */
  mealContent: MealContent,
}

/**
 * 食事プロパティ公開
 */
export type MealPropsPub = Record<string, never>

/**
 * 食事Entity
 */
export class MealEntity extends EntityPub<
  MealPropsPvt,
  MealJsonPvt,
  MealPropsPub,
  MealJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): MealJsonPvt {
    return {
      measureDateTime: this.measureDateTime.value.toJSON(),
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      mealContent: this.mealContent.value,
    };
  }

  toJsonPubData(): MealJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryMealMealEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDateTime.toStringDateTime()}#${this.parentId.value}_${this.childId.value}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDateTime.toStringDateTime()}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 測定日時
   */
  get measureDateTime(): MeasureDateTime {
    return this._value.measureDateTime;
  }

  /**
   * 食事内容
   */
  get mealContent(): MealContent {
    return this._value.mealContent;
  }

  /**
   * 測定者ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }
}
